import React, { useState, useEffect, useCallback } from 'react';

const TITLE_MAX = 60;
const BODY_MIN = 5;
const BODY_MAX = 160;
const TEMPLATES_LIMIT = 20;
const BUTTON_TEXT_MAX = 20;

/* Fixed response values sent to the backend on click — these are NOT
   editable by the admin. Only the display text (label) is dynamic.
   Button 1 always resolves to "yes", Button 2 always resolves to "no",
   regardless of what label the admin types (e.g. "Accept"/"Reject"). */

/* ─────────────────────────────────────────────
   Tiny spinner
───────────────────────────────────────────── */
const Spinner = ({ size = 4, color = 'text-indigo-500' }) => (
  <svg
    className={`animate-spin h-${size} w-${size} ${color}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

/* ─────────────────────────────────────────────
   Main component
   Props:
     isOpen      – boolean
     onClose     – () => void
     apiBaseUrl  – string  e.g. "https://your-api.com"
     onCreated   – (template) => void   // optional, fired after successful save/update
───────────────────────────────────────────── */
const NotificationModal = ({ isOpen, onClose, apiBaseUrl = '', onCreated }) => {
  /* tab state: 'form' shows create/edit form, 'list' shows existing templates */
  const [activeTab, setActiveTab] = useState('form');

  /* form state */
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [titleError, setTitleError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [useButtons, setUseButtons] = useState(false);

  /* dynamic button values — always exactly 2 when useButtons is true */
  const [button1Text, setButton1Text] = useState('');
  const [button2Text, setButton2Text] = useState('');
  const [button1Error, setButton1Error] = useState('');
  const [button2Error, setButton2Error] = useState('');

  /* template-name field */
  const [templateName, setTemplateName] = useState('');
  const [templateNameError, setTemplateNameError] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  /* editing state — when set, Save calls the update endpoint instead of create */
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  /* existing templates list (for Edit Existing tab) */
  const [templates, setTemplates] = useState([]);
  const [tplPage, setTplPage] = useState(1);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplError, setTplError] = useState('');
  const [hasMore, setHasMore] = useState(true);

  /* ── reset on open ─────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('form');
    setTitle('');
    setBody('');
    setTitleError('');
    setBodyError('');
    setUseButtons(false);
    setButton1Text('');
    setButton2Text('');
    setButton1Error('');
    setButton2Error('');
    setTemplateName('');
    setTemplateNameError('');
    setSaving(false);
    setSaveError('');
    setSaved(false);
    setEditingTemplateId(null);
    setTemplates([]);
    setTplPage(1);
    setTplLoading(false);
    setTplError('');
    setHasMore(true);
  }, [isOpen]);

  /* ── fetch existing templates (paginated) ──── */
  const fetchTemplates = useCallback(async (page = 1, reset = false) => {
    if (tplLoading) return;
    setTplLoading(true);
    setTplError('');
    try {
      const token = localStorage.getItem('superadmintoken');
      const qs = new URLSearchParams({ page, limit: TEMPLATES_LIMIT });
      const response = await fetch(
        `${apiBaseUrl}/lenskart-admin/list-push-notification-templates?${qs}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const items = Array.isArray(data) ? data : data?.templates ?? data?.data ?? [];

      setTemplates(prev => (reset ? items : [...prev, ...items]));
      setHasMore(items.length === TEMPLATES_LIMIT);
      setTplPage(page);
    } catch (err) {
      console.error('Template fetch failed:', err);
      setTplError('Failed to load templates. Tap to retry.');
    } finally {
      setTplLoading(false);
    }
  }, [apiBaseUrl, tplLoading]);

  /* Load the list the first time the Edit Existing tab is opened */
  useEffect(() => {
    if (isOpen && activeTab === 'list' && templates.length === 0 && !tplLoading && !tplError) {
      fetchTemplates(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab]);

  const loadMoreTemplates = useCallback(() => {
    if (hasMore && !tplLoading) {
      fetchTemplates(tplPage + 1, false);
    }
  }, [hasMore, tplLoading, tplPage, fetchTemplates]);

  const handleTemplatesScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      loadMoreTemplates();
    }
  }, [loadMoreTemplates]);

  /* ── validation ────────────────────────────── */
  const validateTitle = (v) => {
    if (v.length > TITLE_MAX) return `Cannot exceed ${TITLE_MAX} characters.`;
    return '';
  };

  const validateBody = (v) => {
    if (!v.trim()) return 'Body is required.';
    if (v.trim().length < BODY_MIN) return `Min ${BODY_MIN} characters required.`;
    if (v.length > BODY_MAX) return `Cannot exceed ${BODY_MAX} characters.`;
    return '';
  };

  const validateTemplateName = (v) => {
    if (!v.trim()) return 'Template name is required.';
    if (v.trim().length < 2) return 'Must be at least 2 characters.';
    return '';
  };

  const validateButtonText = (v) => {
    if (!v.trim()) return 'Required.';
    if (v.length > BUTTON_TEXT_MAX) return `Max ${BUTTON_TEXT_MAX} characters.`;
    return '';
  };

  /* ── field change handlers ─────────────────── */
  const handleTitleChange = (e) => {
    const v = e.target.value;
    if (v.length > TITLE_MAX + 5) return;
    setTitle(v);
    if (titleError) setTitleError(validateTitle(v));
  };

  const handleBodyChange = (e) => {
    const v = e.target.value;
    if (v.length > BODY_MAX + 10) return;
    setBody(v);
    if (bodyError) setBodyError(validateBody(v));
  };

  const handleButton1Change = (e) => {
    const v = e.target.value;
    if (v.length > BUTTON_TEXT_MAX + 5) return;
    setButton1Text(v);
    if (button1Error) setButton1Error(validateButtonText(v));
  };

  const handleButton2Change = (e) => {
    const v = e.target.value;
    if (v.length > BUTTON_TEXT_MAX + 5) return;
    setButton2Text(v);
    if (button2Error) setButton2Error(validateButtonText(v));
  };

  const handleToggleUseButtons = () => {
    setUseButtons(prev => {
      const next = !prev;
      // Clear button state whenever buttons are turned off so stale
      // values/errors don't linger if the user re-enables later without
      // meaning to reuse them.
      if (!next) {
        setButton1Error('');
        setButton2Error('');
      }
      return next;
    });
  };

  /* ── Derive the next variable number straight from the body text.
     This is the key fix: instead of tracking a separate `variableCount`
     state that only ever increments, we scan the actual textarea content
     for existing {{n}} placeholders every render. So if the user manually
     deletes {{1}} from the textarea, the next "Add Variable" click will
     correctly offer {{1}} again instead of skipping to {{2}}. ── */
  const getNextVariableNumber = (text) => {
    const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)];
    if (matches.length === 0) return 1;
    const max = Math.max(...matches.map((m) => parseInt(m[1], 10)));
    return max + 1;
  };

  const nextVariableNumber = getNextVariableNumber(body);

  /* ── Add variable placeholder ── */
  const addVariable = () => {
    const placeholder = `{{${nextVariableNumber}}}`;
    const newBody = body + placeholder;

    // Check if adding this variable would exceed body limit
    if (newBody.length > BODY_MAX) {
      alert(`Cannot add variable. Body would exceed ${BODY_MAX} characters`);
      return;
    }

    setBody(newBody);
  };

  /* ── Render message with variable highlighting ── */
  const renderMessageWithVariables = (message) => {
    if (!message) return null;

    const parts = message.split(/(\{\{\d+\}\})/g);

    return parts.map((part, index) => {
      const match = part.match(/\{\{(\d+)\}\}/);
      if (match) {
        const varNumber = match[1];
        return (
          <span
            key={index}
            className="inline-block px-2 py-0.5 mx-0.5 bg-indigo-100 text-indigo-700 rounded-md text-xs font-semibold border border-indigo-200"
          >
            {'{{'}{varNumber}{'}}'}
          </span>
        );
      }
      return (
        <span key={index} className="break-words whitespace-pre-wrap inline">
          {part}
        </span>
      );
    });
  };

  const canSave = () => {
    if (saving) return false;
    if (validateTitle(title) || validateBody(body) || validateTemplateName(templateName)) return false;
    if (useButtons) {
      if (validateButtonText(button1Text) || validateButtonText(button2Text)) return false;
    }
    return true;
  };

  /* ── Select a template from the list to edit ── */
  const handleSelectTemplateForEdit = (template) => {
  // Extract button values from the response structure
  let button1Value = '';
  let button2Value = '';
  let hasButtonValues = false;
  
  // Check if buttons exist and have quickReplies
  if (template.buttons && template.buttons.quickReplies && template.buttons.quickReplies.length > 0) {
    // Get button 1 (index 0)
    const button1 = template.buttons.quickReplies.find(b => b.index === 0);
    if (button1 && button1.payload) {
      button1Value = button1.payload;
      hasButtonValues = true;
    }
    
    // Get button 2 (index 1)
    const button2 = template.buttons.quickReplies.find(b => b.index === 1);
    if (button2 && button2.payload) {
      button2Value = button2.payload;
      hasButtonValues = true;
    }
  }
  
  // Fallback: check if there are direct button1/button2 fields (for backward compatibility)
  if (!hasButtonValues) {
    if (template.button1) {
      button1Value = template.button1;
      hasButtonValues = true;
    }
    if (template.button2) {
      button2Value = template.button2;
      hasButtonValues = true;
    }
  }

  setTemplateName(template.templateName || template.name || '');
  setTitle(template.template_title || template.templateTitle || template.notificationTitle || '');
  setBody(template.templateMessage || template.notificationBody || '');

  const shouldUseButtons = template.useButtons === true || hasButtonValues;
  setUseButtons(shouldUseButtons);
  setButton1Text(button1Value);
  setButton2Text(button2Value);

  setTitleError('');
  setBodyError('');
  setTemplateNameError('');
  setButton1Error('');
  setButton2Error('');
  setSaveError('');
  setSaved(false);

  setEditingTemplateId(template._id || template.id);
  setActiveTab('form');
};

  const handleStartNewTemplate = () => {
    setTemplateName('');
    setTitle('');
    setBody('');
    setUseButtons(false);
    setButton1Text('');
    setButton2Text('');
    setButton1Error('');
    setButton2Error('');
    setTitleError('');
    setBodyError('');
    setTemplateNameError('');
    setSaveError('');
    setSaved(false);
    setEditingTemplateId(null);
    setActiveTab('form');
  };

  if (!isOpen) return null;

  const isEditing = !!editingTemplateId;

  const handleSave = async () => {
  const te = validateTitle(title);
  const be = validateBody(body);
  const tne = validateTemplateName(templateName);
  const b1e = useButtons ? validateButtonText(button1Text) : '';
  const b2e = useButtons ? validateButtonText(button2Text) : '';
  setTitleError(te);
  setBodyError(be);
  setTemplateNameError(tne);
  setButton1Error(b1e);
  setButton2Error(b2e);
  if (te || be || tne || b1e || b2e) return;

  setSaving(true);
  setSaveError('');
  try {
    const payload = {
      filterSource: "send_notification",
      hasAppToken: true,
      notificationTitle: title.trim() || undefined,
      notificationBody: body.trim(),
      useButtons: useButtons,
      templateName: templateName.trim(),
    };

    if (useButtons) {
      payload.button1 = button1Text.trim();
      payload.button2 = button2Text.trim();
    }

    const endpoint = isEditing
      ? '/lenskart-admin/update-push-notification-template'
      : '/lenskart-admin/add-push-notification-template';

    if (isEditing) {
      payload.templateId = editingTemplateId;
    }

    const res = await fetch(`${apiBaseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('superadmintoken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const result = await res.json().catch(() => null);
    setSaved(true);

    onCreated?.(result);

    setTimeout(() => {
      onClose();
    }, 1000);

  } catch (err) {
    console.error('Save failed:', err);
    setSaveError(isEditing ? 'Failed to update template. Please try again.' : 'Failed to save template. Please try again.');
  } finally {
    setSaving(false);
  }
};

  /* ═══════════════════════════════════════════
     Render
  ═══════════════════════════════════════════ */
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-xl w-full flex flex-col shadow-2xl overflow-hidden"
        style={{ maxWidth: '760px', maxHeight: '90vh' }}
      >

        {/* ── Header ─────────────────────────── */}
        <div
          className="px-5 py-3.5 flex items-center justify-between flex-shrink-0"
          style={{ backgroundColor: '#E2E1FD' }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            {activeTab === 'list' ? 'Notification Templates' : isEditing ? 'Edit Notification Template' : 'Create Notification Template'}
          </h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-gray-600 hover:text-gray-900 transition-colors text-xl font-light leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* ── Tabs ───────────────────────────── */}
        <div className="flex border-b flex-shrink-0 bg-white">
          <button
            onClick={() => setActiveTab('form')}
            disabled={saving}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'form'
                ? 'text-indigo-600 border-b-2 border-indigo-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {isEditing ? 'Edit Template' : 'New Template'}
          </button>
          <button
            onClick={() => setActiveTab('list')}
            disabled={saving}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'list'
                ? 'text-indigo-600 border-b-2 border-indigo-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Edit Existing
          </button>
        </div>

        {activeTab === 'list' ? (
          /* ── Templates list ─────────────────── */
          <div
            className="flex-1 overflow-y-auto p-4 space-y-2"
            onScroll={handleTemplatesScroll}
          >
            {tplError ? (
              <div className="text-center py-8">
                <p className="text-red-500 text-sm">{tplError}</p>
                <button
                  onClick={() => fetchTemplates(1, true)}
                  className="mt-2 text-indigo-500 hover:text-indigo-700 text-sm font-medium"
                >
                  Retry
                </button>
              </div>
            ) : templates.length === 0 && !tplLoading ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                No templates available
              </div>
            ) : (
              <>
                {templates.map((template) => (
                  <div
                    key={template._id || template.id}
                    onClick={() => handleSelectTemplateForEdit(template)}
                    className="p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50 hover:border-indigo-300 transition-all"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-medium text-sm text-gray-800 break-words">
                        {template.templateName || template.name || 'Unnamed Template'}
                      </h4>
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    {(template.templateMessage || template.notificationBody) && (
                      <p className="text-xs text-gray-500 mt-1 break-words line-clamp-2">
                        {template.templateMessage || template.notificationBody}
                      </p>
                    )}
                  </div>
                ))}
                {tplLoading && (
                  <div className="text-center py-4">
                    <div className="animate-spin h-6 w-6 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-1">Loading more...</p>
                  </div>
                )}
                {!hasMore && templates.length > 0 && (
                  <div className="text-center py-4 text-xs text-gray-400">
                    No more templates to load
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            {/* ── Body ───────────────────────────── */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 min-w-0">

              {isEditing && (
                <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2">
                  <p className="text-xs text-indigo-700">
                    Editing existing template
                  </p>
                  <button
                    onClick={handleStartNewTemplate}
                    disabled={saving}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                  >
                    + New Template instead
                  </button>
                </div>
              )}

              {/* Template Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={e => {
                    setTemplateName(e.target.value);
                    if (templateNameError) setTemplateNameError(validateTemplateName(e.target.value));
                  }}
                  onBlur={() => setTemplateNameError(validateTemplateName(templateName))}
                  placeholder="Add Template Name"
                  disabled={saving}
                  className={`w-full px-3 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                    templateNameError
                      ? 'border-red-300 bg-red-50 focus:ring-red-300'
                      : 'border-gray-300 focus:ring-indigo-400 focus:border-indigo-400'
                  } ${saving ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {templateNameError && (
                  <p className="text-xs text-red-500 mt-1">{templateNameError}</p>
                )}
              </div>

              {/* Notification Title – Optional */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Notification Title{' '}
                  <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={handleTitleChange}
                  onBlur={() => setTitleError(validateTitle(title))}
                  placeholder="Add Title"
                  disabled={saving}
                  className={`w-full px-3 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                    titleError
                      ? 'border-red-300 bg-red-50 focus:ring-red-300'
                      : 'border-gray-300 focus:ring-indigo-400 focus:border-indigo-400'
                  } ${saving ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                <div className="flex justify-between items-center mt-1">
                  {titleError
                    ? <p className="text-xs text-red-500">{titleError}</p>
                    : <p className="text-xs text-gray-400">Leave blank to send without a title</p>
                  }
                  <p className={`text-xs font-medium ${title.length > TITLE_MAX ? 'text-red-500' : 'text-gray-400'}`}>
                    {title.length}/{TITLE_MAX}
                  </p>
                </div>
              </div>

              {/* Notification Body – Required */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-semibold text-gray-700">
                    Notification Body <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addVariable}
                    disabled={saving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-500 text-white text-xs font-semibold rounded-md hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Variable {'{{'}{nextVariableNumber}{'}}'}
                  </button>
                </div>
                <textarea
                  value={body}
                  onChange={handleBodyChange}
                  onBlur={() => setBodyError(validateBody(body))}
                  rows={5}
                  placeholder="Add Notification Body"
                  disabled={saving}
                  className={`w-full px-3 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all resize-none ${
                    bodyError
                      ? 'border-red-300 bg-red-50 focus:ring-red-300'
                      : 'border-gray-300 focus:ring-indigo-400 focus:border-indigo-400'
                  } ${saving ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                <div className="flex justify-between items-center mt-1">
                  {bodyError
                    ? <p className="text-xs text-red-500">{bodyError}</p>
                    : <p className="text-xs text-gray-400">
                        Min {BODY_MIN} chars required. Click "Add Variable" to insert dynamic placeholders
                      </p>
                  }
                  <p className={`text-xs font-medium ${body.length > BODY_MAX ? 'text-red-500' : 'text-gray-400'}`}>
                    {body.length}/{BODY_MAX}
                  </p>
                </div>
              </div>

              {/* Preview with variables highlighted */}
              {body && (
                <div className="bg-indigo-50 rounded-md p-4 border border-indigo-200">
                  <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Template Preview:
                  </p>
                  <div className="text-sm text-gray-700 break-words whitespace-pre-wrap bg-white p-3 rounded border border-indigo-100 min-h-[40px]">
                    {renderMessageWithVariables(body)}
                  </div>
                </div>
              )}

              {/* Yes/No buttons toggle */}
              <div className="border border-gray-200 rounded-md px-3 py-2.5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Add Buttons</p>
                    <p className="text-xs text-gray-400">Worker can respond directly from the notification</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleUseButtons}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      useButtons ? 'bg-indigo-500' : 'bg-gray-300'
                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        useButtons ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Dynamic button value inputs — only shown when useButtons is on */}
                {useButtons && (
                  <div className="grid grid-cols-2 gap-3 pt-1 border-t border-gray-100">
                    <div className="pt-2">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Button 1 Text <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={button1Text}
                        onChange={handleButton1Change}
                        onBlur={() => setButton1Error(validateButtonText(button1Text))}
                        placeholder="e.g. Yes"
                        disabled={saving}
                        className={`w-full px-2.5 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                          button1Error
                            ? 'border-red-300 bg-red-50 focus:ring-red-300'
                            : 'border-gray-300 focus:ring-indigo-400 focus:border-indigo-400'
                        } ${saving ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {button1Error && <p className="text-xs text-red-500">{button1Error}</p>}
                        <p className={`text-xs ml-auto ${button1Text.length > BUTTON_TEXT_MAX ? 'text-red-500' : 'text-gray-400'}`}>
                          {button1Text.length}/{BUTTON_TEXT_MAX}
                        </p>
                      </div>
                    </div>

                    <div className="pt-2">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Button 2 Text <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={button2Text}
                        onChange={handleButton2Change}
                        onBlur={() => setButton2Error(validateButtonText(button2Text))}
                        placeholder="e.g. No"
                        disabled={saving}
                        className={`w-full px-2.5 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                          button2Error
                            ? 'border-red-300 bg-red-50 focus:ring-red-300'
                            : 'border-gray-300 focus:ring-indigo-400 focus:border-indigo-400'
                        } ${saving ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {button2Error && <p className="text-xs text-red-500">{button2Error}</p>}
                        <p className={`text-xs ml-auto ${button2Text.length > BUTTON_TEXT_MAX ? 'text-red-500' : 'text-gray-400'}`}>
                          {button2Text.length}/{BUTTON_TEXT_MAX}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {saveError && (
                <p className="text-xs text-red-500">{saveError}</p>
              )}

            </div>

            {/* ── Footer ─────────────────────────── */}
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end flex-shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSave}
                  disabled={!canSave()}
                  className={`px-5 py-2 rounded-md transition-all text-sm font-semibold shadow-sm flex items-center gap-2 ${
                    saving
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : saved
                      ? 'bg-green-500 text-white'
                      : 'bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {saving && <Spinner size={4} color="text-white" />}
                  {saving
                    ? (isEditing ? 'Updating…' : 'Saving…')
                    : saved
                    ? '✓ Saved!'
                    : (isEditing ? 'Update Template' : 'Create Template')}
                </button>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default NotificationModal;